import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ROUTES } from '@constants/index'
import { AppError, AppErrorType } from '@typesApp/AppError'
import { ViewState } from '@typesApp/ViewState'
import { AppCloseAction, PageFlowConfiguration, ViewName } from '@typesApp/PageFlow'
import { AppState } from '@redux/store'
import { configApi } from '@redux/queries/config'
import { insurersApi } from '@redux/queries/insurers'
import { claimRecordsApi } from '@redux/queries/claimRecords'

export type ProgressStep = number

type VoidCallback = () => void
type VoidPromiseCallback = () => Promise<void>
type LoadingPageState = {
  action?: VoidPromiseCallback
  disableAutoClose?: boolean
}

type UnsavedChangesState = {
  enableUnsavedChangesModal?: boolean
  showUnsavedChangesModal?: boolean
  hasUnsavedChanges?: boolean
  onCloseCallback?: VoidCallback
}

type WorkflowState = {
  step: ProgressStep
  showHeader: boolean
  viewState: ViewState
  appVisible: boolean
  appVisibleAllowed: boolean
  appClosing: boolean
  error?: string | unknown
  pageFlow?: PageFlowConfiguration
  unsavedChanges?: UnsavedChangesState
  hideNavigateBack?: boolean
  onBeforeCloseCallback?: VoidPromiseCallback
  onNavigateBackCallback?: VoidCallback
  viewName?: ViewName
  loadingPage?: LoadingPageState
  appError?: AppError
  hideDrawerClose?: boolean
}

const DEFAULT_VIEW_STATE: ViewState = {
  isEditing: false,
  isReadonly: false,
}

const EDIT_VIEW_STATE: ViewState = {
  isEditing: true,
  isReadonly: false,
  previousRoute: ROUTES.REVIEW_CLAIM,
}

const READONLY_VIEW_STATE: ViewState = {
  isEditing: false,
  isReadonly: true,
}

const initialState: WorkflowState = {
  step: 0,
  showHeader: true,
  viewState: DEFAULT_VIEW_STATE,
  appVisible: false,
  appVisibleAllowed: false,
  appClosing: false,
  hideNavigateBack: true,
  unsavedChanges: {
    enableUnsavedChangesModal: false,
  },
}

const REDUCER_PATH = 'workflow'

export const workflowSlice = createSlice({
  name: REDUCER_PATH,
  initialState: initialState,
  reducers: {
    setProgressStep(state, action: PayloadAction<ProgressStep>) {
      state.showHeader = action.payload > 0 ? true : state.showHeader
      state.step = action.payload
    },
    hideProgressStep(state) {
      state.step = 0
    },
    hideDrawerHeader(state) {
      state.showHeader = false
    },
    hideDrawerClose(state) {
      state.hideDrawerClose = true
    },
    resetDrawerClose(state) {
      state.hideDrawerClose = undefined
    },
    showApp(state) {
      state.appVisible = true
    },
    closeApp(state) {
      if (state.appVisible) {
        state.appClosing = true
      }
    },
    enableUnsavedChangesModal(state, action: PayloadAction<boolean>) {
      state.unsavedChanges = {
        ...state.unsavedChanges,
        enableUnsavedChangesModal: action.payload,
      }
    },
    setHasUnsavedChanges(state, action: PayloadAction<{ hasUnsavedChanges: boolean; onCloseCallback?: () => void }>) {
      state.unsavedChanges = {
        ...state.unsavedChanges,
        hasUnsavedChanges: action.payload.hasUnsavedChanges,
        onCloseCallback: action.payload.onCloseCallback,
      }
    },
    showUnsavedChangesModal(state) {
      state.unsavedChanges = {
        ...state.unsavedChanges,
        showUnsavedChangesModal:
          Boolean(state.unsavedChanges?.hasUnsavedChanges) && state.unsavedChanges?.enableUnsavedChangesModal,
      }
    },
    showUnsavedChangesModalWithCallback(state, action: PayloadAction<{ onCloseCallback?: () => void }>) {
      state.unsavedChanges = {
        ...state.unsavedChanges,
        onCloseCallback: action.payload.onCloseCallback,
        showUnsavedChangesModal:
          Boolean(state.unsavedChanges?.hasUnsavedChanges) && state.unsavedChanges?.enableUnsavedChangesModal,
      }
    },
    hideUnsavedChangesModal(state, action: PayloadAction<AppCloseAction>) {
      state.unsavedChanges = {
        ...state.unsavedChanges,
        showUnsavedChangesModal: false,
      }

      // continue to close app or cancel close
      if (action.payload === AppCloseAction.CLOSE_APP && !state.appClosing) {
        state.appClosing = true
        state.unsavedChanges?.onCloseCallback?.()
      } else if (action.payload === AppCloseAction.CANCEL && state.appClosing) {
        state.appClosing = false
      }
    },
    setHideNavigateBack(state, action: PayloadAction<boolean>) {
      state.hideNavigateBack = action.payload
    },
    setNavigateBackCallback(state, action: PayloadAction<VoidCallback>) {
      state.onNavigateBackCallback = action.payload
    },
    setViewName(state, action: PayloadAction<ViewName | undefined>) {
      state.viewName = action.payload
    },
    setEditMode(state) {
      state.viewState = EDIT_VIEW_STATE
    },
    setReadonlyMode(state) {
      state.viewState = READONLY_VIEW_STATE
    },
    resetViewState(state) {
      state.viewState = DEFAULT_VIEW_STATE
    },
    setBeforeCloseCallback(state, action: PayloadAction<VoidPromiseCallback>) {
      state.onBeforeCloseCallback = action.payload
    },
    resetBeforeCloseCallback(state) {
      state.onBeforeCloseCallback = undefined
    },
    setLoadingPageAction(state, action: PayloadAction<LoadingPageState>) {
      state.loadingPage = action.payload
    },
    clearLoadingPageAction(state) {
      state.loadingPage = undefined
    },
    unhandledError(state, action: PayloadAction<unknown>) {
      state.error = action.payload
    },
    setAppError(state, action: PayloadAction<AppError | AppErrorType>) {
      if (typeof action.payload === 'object') {
        state.appError = action.payload
      } else {
        state.appError = {
          errorType: action.payload,
        }
      }
    },
    resetAppError(state) {
      state.appError = undefined
    },
    setAppVisibleAllowed(state, action: PayloadAction<boolean | undefined>) {
      state.appVisibleAllowed = action === undefined || action.payload === undefined ? true : action.payload
    },
    resetWorkflow(state) {
      return {
        ...initialState,
        pageFlow: state.pageFlow,
      }
    },
    resetWorkflowInitialState(state) {
      return initialState
    },
  },
  extraReducers: builder => {
    /**
     * Cache page flow configuration on fetch
     */
    builder.addMatcher(configApi.endpoints.getPageFlowConfiguration.matchFulfilled, (state, action) => {
      state.pageFlow = action.payload
    })

    /**
     * Set max steps on dynamic forms fetch
     */
    builder.addMatcher(insurersApi.endpoints.getDefaultForms.matchFulfilled, (state, action) => {
      const formCount = action.payload?.length || 0
      if (state.pageFlow) {
        state.pageFlow.stepsOffset = formCount || 0
        state.pageFlow.maxSteps = (state.pageFlow?.steps?.length ?? 0) + formCount - 1
      }
    })

    /**
     * Reset unsaved changes state on claim record PATCH
     */
    builder.addMatcher(claimRecordsApi.endpoints.updateClaimRecord.matchFulfilled, (state, action) => {
      state.unsavedChanges = {
        ...initialState.unsavedChanges,
        enableUnsavedChangesModal: state.unsavedChanges?.enableUnsavedChangesModal,
        hasUnsavedChanges: false,
      }
    })
  },
})

export const {
  setProgressStep,
  hideProgressStep,
  hideDrawerHeader,
  hideDrawerClose,
  resetDrawerClose,
  showApp,
  closeApp,
  setEditMode,
  setReadonlyMode,
  resetViewState,
  setBeforeCloseCallback,
  resetBeforeCloseCallback,
  unhandledError,
  setAppVisibleAllowed,
  enableUnsavedChangesModal,
  setHasUnsavedChanges,
  showUnsavedChangesModal,
  hideUnsavedChangesModal,
  showUnsavedChangesModalWithCallback,
  setHideNavigateBack,
  setNavigateBackCallback,
  setLoadingPageAction,
  clearLoadingPageAction,
  setViewName,
  setAppError,
  resetAppError,
  resetWorkflow,
  resetWorkflowInitialState,
} = workflowSlice.actions

export const drawerHeaderVisibleSelector = (state: AppState): boolean => state.workflow.showHeader
export const drawerHideCloseSelector = (state: AppState): boolean => state.workflow.hideDrawerClose === true
export const progressStepSelector = (state: AppState): ProgressStep => state.workflow.step
export const viewStateSelector = (state: AppState): ViewState => state.workflow.viewState
export const unhandledErrorSelector = (state: AppState): unknown => state.workflow.error
export const appErrorSelector = (state: AppState): AppError | undefined => state.workflow.appError
export const appVisibleSelector = (state: AppState): boolean => state.workflow.appVisible
export const appVisibleAllowedSelector = (state: AppState): boolean => state.workflow.appVisibleAllowed
export const appClosingSelector = (state: AppState): boolean => state.workflow.appClosing
export const appBeforeCloseCallbackSelector = (state: AppState): VoidPromiseCallback | undefined =>
  state.workflow.onBeforeCloseCallback
export const enableUnsavedChangesModalSelector = (state: AppState): boolean =>
  Boolean(state.workflow.unsavedChanges?.enableUnsavedChangesModal)
export const hasUnsavedChangesSelector = (state: AppState): boolean =>
  state.workflow.unsavedChanges?.hasUnsavedChanges || false
export const showUnsavedChangesModalSelector = (state: AppState): boolean =>
  state.workflow.unsavedChanges?.showUnsavedChangesModal || false
export const pageFlowConfigurationSelector = (state: AppState): PageFlowConfiguration | undefined =>
  state.workflow.pageFlow
export const hideNavigateBackSelector = (state: AppState): boolean => Boolean(state.workflow.hideNavigateBack)
export const navigateBackCallbackSelector = (state: AppState): VoidCallback | undefined =>
  state.workflow.onNavigateBackCallback
export const viewNameSelector = (state: AppState): ViewName | undefined => state.workflow.viewName
export const loadingPageActionSelector = (state: AppState): LoadingPageState | undefined => state.workflow.loadingPage
